import React, { useState, useEffect } from 'react';
import FinancialAssetsModal from './FinancialAssetsModal';

const assetTypes = [
  { key: 'cash', label: 'Cash' },
  { key: 'deposit', label: 'Deposit' },
  { key: 'brokerageAccount', label: 'Brokerage Account' },
  { key: 'nonPublicTradedStock', label: 'Non-Public Traded Stock' },
  { key: 'bond', label: 'Bond' },
  { key: 'retirementAccount', label: 'Retirement Account' },
  { key: 'securityDeposit', label: 'Security Deposit' },
  { key: 'annuity', label: 'Annuity' },
  { key: 'educationIra', label: 'Education IRA' },
  { key: 'trust', label: 'Trust' },
  { key: 'patent', label: 'Patent' },
  { key: 'franchise', label: 'Franchise' },
  { key: 'taxRefund', label: 'Tax Refund' },
  { key: 'obligation', label: 'Obligation' },
  { key: 'otherAmountOwed', label: 'Other Amount Owed' },
  { key: 'insuranceInterest', label: 'Interest in Insurance Policy' },
  { key: 'inheritanceInterest', label: 'Inheritance Interest' },
  { key: 'lawsuitInterest', label: 'Lawsuit Interest' },
  { key: 'contingentClaim', label: 'Contingent and Unliquidated Claim' },
  { key: 'otherFinancialAsset', label: 'Other Financial Asset' },
];

const endpointMap = {
  cash: 'cash-assets',
  deposit: 'deposits',
  brokerageAccount: 'brokerage-accounts',
  nonPublicTradedStock: 'nonpublic-stocks',
  bond: 'bonds',
  retirementAccount: 'retirement-accounts',
  securityDeposit: 'security-deposits',
  annuity: 'annuities',
  educationIra: 'education-iras',
  trust: 'trusts',
  patent: 'patents',
  franchise: 'franchises',
  taxRefund: 'tax-refunds',
  obligation: 'obligations',
  otherAmountOwed: 'other-amounts-owed',
  insuranceInterest: 'insurance-interests',
  inheritanceInterest: 'inheritance-interests',
  lawsuitInterest: 'lawsuit-interests',
  contingentClaim: 'contingent-claims',
  otherFinancialAsset: 'other-financial-assets',
};

// Maps assetTypes keys to the combined endpoint JSON keys
const jsonKeyMap = {
  cash: 'cash',
  deposit: 'deposits',
  brokerageAccount: 'brokerage_accounts',
  nonPublicTradedStock: 'nonpublic_stocks',
  bond: 'bonds',
  retirementAccount: 'retirement_accounts',
  securityDeposit: 'security_deposits',
  annuity: 'annuities',
  educationIra: 'education_iras',
  trust: 'trusts',
  patent: 'patents',
  franchise: 'franchises',
  taxRefund: 'tax_refunds',
  obligation: 'obligations',
  otherAmountOwed: 'other_amounts_owed',
  insuranceInterest: 'insurance_interests',
  inheritanceInterest: 'inheritance_interests',
  lawsuitInterest: 'lawsuit_interests',
  contingentClaim: 'contingent_claims',
  otherFinancialAsset: 'other_financial_assets',
};

export default function FinancialAssetsSection({ caseId, onSave, exemptions, scope = '' }) {
  const [assets, setAssets] = useState({});
  const [selectedAssetType, setSelectedAssetType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  // Fetch all financial assets and map API keys to assetTypes keys
  useEffect(() => {
    (async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/case/${caseId}/assets/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      if (!res.ok) return;
      const json = await res.json();
      const fin = json.financial || {};
      const mapped = {};
      assetTypes.forEach(({ key }) => {
        // Use jsonKeyMap to map assetTypes keys to JSON properties from the combined endpoint
        mapped[key] = fin[jsonKeyMap[key]] || [];
      });
      setAssets(mapped);
    })();
  }, [caseId]);

  const openAdd = (typeKey) => {
    if (!typeKey) return;
    setModalType(typeKey);
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openEdit = (typeKey, item) => {
    setModalType(typeKey);
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleSave = (saved) => {
    setAssets(prev => {
      const list = prev[modalType] || [];
      const updatedList = editingItem
        ? list.map(i => i.id === saved.id ? saved : i)
        : [...list, saved];
      const newAssets = { ...prev, [modalType]: updatedList };
      onSave(newAssets);
      return newAssets;
    });
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleDelete = async (typeKey, id) => {
    await fetch(
      `${process.env.REACT_APP_API_URL}/case/${caseId}/${endpointMap[typeKey]}/${id}/delete/`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      }
    );
    setAssets(prev => ({
      ...prev,
      [typeKey]: prev[typeKey].filter(i => i.id !== id),
    }));
  };

  return (
    <section className="mt-12 space-y-8">
      <header className="flex items-center justify-between bg-gray-100 p-4 rounded-md">
        <h2 className="text-2xl font-semibold">Financial Assets</h2>
        <div className="flex space-x-2">
          <select
            value={selectedAssetType}
            onChange={e => setSelectedAssetType(e.target.value)}
            className="border border-gray-300 rounded-md p-2"
          >
            <option value="">Select Asset…</option>
            {assetTypes.map(at => (
              <option key={at.key} value={at.key}>{at.label}</option>
            ))}
          </select>
          <button
            onClick={() => openAdd(selectedAssetType)}
            disabled={!selectedAssetType}
            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Asset
          </button>
        </div>
      </header>

      {assetTypes.map(({ key, label }) =>
        assets[key] && assets[key].length > 0 && (
          <div key={key} className="space-y-4">
            <h3 className="text-xl font-semibold">{label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets[key].map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow p-6 flex flex-col hover:shadow-lg transition-shadow"
                >
                  <ul className="space-y-1 text-sm text-gray-700 mb-4 flex-grow">
                    {Object.entries(item)
                      .filter(([field]) => !['id', 'case', 'form_number'].includes(field))
                      .map(([field, value]) => (
                        <li key={field}>
                          <strong>{field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</strong> {value}
                        </li>
                      ))}
                  </ul>
                  <div className="mt-auto flex space-x-2">
                    <button
                      onClick={() => openEdit(key, item)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(key, item.id)}
                      className="px-3 py-1 bg-red-600 text-white rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      <FinancialAssetsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        type={modalType}
        item={editingItem}
        onSave={handleSave}
        caseId={caseId}
        exemptions={exemptions}
        scope={scope}
      />
    </section>
);
}